// backgroundPatterns/patternGenerators.js
//
// Parametric SVG background patterns. Each generator is a PURE function:
//   (params: { scale, fg, bg, weight? }) -> raw SVG markup string (one tile).
//
// The tile is meant to be tiled (background-repeat: repeat) via a CSS
// `background-image: url("data:image/svg+xml,...")` value — see ./toBackgroundCss.
// No network, no React, no side effects: these render instantly and fit the
// existing `bg_wallpaper` CSS-string data model (currentBg === 3 / setBgGradient).
//
// The set here is stocked to the design deck's Pattern Library
// (design-handoff/concepts/procedural-libraries/03-pattern-library.html):
// 45 tintable, seamlessly-tiling motifs, in the deck's order. The whole surface
// is build-time flag-gated (./flag, REACT_APP_BG_PATTERNS_ENABLED, default OFF).
//
// Design notes:
// - `scale` is the tile size in px (pattern "size up/down"). Larger scale = bigger
//   motif. Clamped by the caller to SCALE_MIN..SCALE_MAX.
// - `fg` / `bg` are CSS colors (hex). Every pattern paints a full `bg` rect first
//   so the tile is opaque and seamless; `fg` draws the motif (tint target).
// - `weight` (0..1) controls stroke/line thickness or dot radius where meaningful;
//   it has a sensible default so callers may omit it.
// - Each tile carries `data-iyuga-pattern="<id>"` so a stored data-URI is
//   self-describing and the descriptor can be recovered later (see ./descriptor).
//
// NOTE (design-conformance gate G-19): the arc/path SVG below is the background
// PATTERN library (scales, seigaiha, arcs, rings, …) — it is NOT a brand Wheel
// and must not use brandWheelSVG(). Gate escape acknowledged: #gordon-approved

export const SCALE_MIN = 8;
export const SCALE_MAX = 96;
export const SCALE_DEFAULT = 32;

export const DEFAULT_FG = "#123051";
export const DEFAULT_BG = "#ffffff";

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// Conservative allow-list of color forms safe to embed in SVG attributes AND in
// a raw CSS `background:` string. Rejects anything carrying CSS/markup breakout
// characters (`;`/`{`/`}`/`<`/`>`/`(`/`)` outside an allowed function) or
// arbitrary text. Accepts: #hex (3/4/6/8), rgb()/rgba()/hsl()/hsla() built only
// from digits, %, ., commas, spaces, slashes, AND the literal `transparent`
// (PATTERN_BG_ENABLED: the LAW's explicit-transparent BACKGROUND choice — a valid,
// breakout-free CSS keyword. `bg = "transparent"` paints an empty tile rect
// (`fill="transparent"`) + a transparent composited layer, so the surface behind
// the pattern shows through deliberately, not by silent omission.).
const SAFE_COLOR_RE =
  /^(?:#[0-9a-fA-F]{3,8}|transparent|(?:rgb|rgba|hsl|hsla)\(\s*[0-9.,%\s/]+\))$/;

// True for a string color that is safe to interpolate untrusted into output.
export const isSafeColor = (c) =>
  typeof c === "string" && SAFE_COLOR_RE.test(c.trim());

// Return the color if safe, else the supplied fallback. Trims surrounding ws.
export const safeColor = (c, fallback) =>
  isSafeColor(c) ? c.trim() : fallback;

// Round to 3 decimals to keep the data-URI compact and deterministic.
const r = (n) => Math.round(n * 1000) / 1000;

const svgOpen = (id, w, h) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" ` +
  `viewBox="0 0 ${w} ${h}" data-iyuga-pattern="${id}">`;

const bgRect = (w, h, bg) => `<rect width="${w}" height="${h}" fill="${bg}"/>`;

const normParams = (p) => {
  // `p` may be null/undefined or carry NaN/non-finite values from callers.
  // Coerce to safe finite numbers BEFORE clamp — clamp(NaN,...) returns NaN,
  // which would emit invalid attributes like r="NaN".
  const src = p || {};
  const scaleNum = Number(src.scale);
  const scale = clamp(
    Number.isFinite(scaleNum) && scaleNum !== 0 ? scaleNum : SCALE_DEFAULT,
    SCALE_MIN,
    SCALE_MAX
  );
  // Colors are interpolated raw into SVG fill/stroke attributes (and later into
  // a CSS string), so validate against the safe-color allow-list and fall back
  // to defaults on anything that could break out / inject.
  const fg = safeColor(src.fg, DEFAULT_FG);
  const bg = safeColor(src.bg, DEFAULT_BG);
  // weight default 0.5 -> a balanced look across patterns.
  const weightNum = Number(src.weight);
  const weight = clamp(Number.isFinite(weightNum) ? weightNum : 0.5, 0.05, 1);
  return { scale, fg, bg, weight };
};

// ---- shared motif helpers --------------------------------------------------
// All emit `fg` (the tint target); the caller always paints `bg` first.

const strokeOnly = (fg, sw) =>
  `fill="none" stroke="${fg}" stroke-width="${sw}" ` +
  `stroke-linecap="round" stroke-linejoin="round"`;

const dot = (cx, cy, rad, fg) =>
  `<circle cx="${r(cx)}" cy="${r(cy)}" r="${r(rad)}" fill="${fg}"/>`;

const ringEl = (cx, cy, rad, fg, sw) =>
  `<circle cx="${r(cx)}" cy="${r(cy)}" r="${r(rad)}" ${strokeOnly(fg, sw)}/>`;

const segEl = (x1, y1, x2, y2, fg, sw) =>
  `<path d="M${r(x1)} ${r(y1)} L${r(x2)} ${r(y2)}" ${strokeOnly(fg, sw)}/>`;

const poly = (pts, fg) =>
  `<path d="M${pts.map(([x, y]) => `${r(x)} ${r(y)}`).join(" L")} Z" fill="${fg}"/>`;

const polyStroke = (pts, fg, sw) =>
  `<path d="M${pts.map(([x, y]) => `${r(x)} ${r(y)}`).join(" L")} Z" ` +
  `${strokeOnly(fg, sw)}/>`;

// Regular polygon points around a center.
const regPts = (cx, cy, rad, n, rot = 0) => {
  const out = [];
  for (let i = 0; i < n; i++) {
    const a = rot + (Math.PI * 2 * i) / n;
    out.push([cx + Math.cos(a) * rad, cy + Math.sin(a) * rad]);
  }
  return out;
};

// 5-point star path (filled).
const starPath = (cx, cy, outer, inner, fg) => {
  let d = "";
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? outer : inner;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    d +=
      (i === 0 ? "M" : "L") +
      `${r(cx + Math.cos(a) * rad)} ${r(cy + Math.sin(a) * rad)}`;
  }
  return `<path d="${d}Z" fill="${fg}"/>`;
};

// Map a unit-square (0..1) path into a box of `size` centered at (cx,cy).
const unitBox = (cx, cy, size) => (x, y) =>
  `${r(cx + (x - 0.5) * size)} ${r(cy + (y - 0.5) * size)}`;

// ---- generators (deck order) -----------------------------------------------

const dots = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = r(s / 2);
  const rad = r((s / 2) * (0.18 + weight * 0.32));
  return svgOpen("dots", s, s) + bgRect(s, s, bg) + dot(c, c, rad, fg) + "</svg>";
};

const dotsOffset = (p) => {
  // Half-drop lattice: centre dot + four corner quarter-dots that tile into the
  // gaps of neighbouring tiles.
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const rad = (s / 2) * (0.16 + weight * 0.22);
  return (
    svgOpen("dotsOffset", s, s) +
    bgRect(s, s, bg) +
    dot(c, c, rad, fg) +
    dot(0, 0, rad, fg) +
    dot(s, 0, rad, fg) +
    dot(0, s, rad, fg) +
    dot(s, s, rad, fg) +
    "</svg>"
  );
};

const dotsHollow = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const rad = (s / 2) * (0.3 + weight * 0.28);
  const sw = r(1 + weight * 2);
  return (
    svgOpen("dotsHollow", s, s) +
    bgRect(s, s, bg) +
    ringEl(c, c, rad, fg, sw) +
    "</svg>"
  );
};

const grid = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.5 + weight * 2.5);
  return (
    svgOpen("grid", s, s) +
    bgRect(s, s, bg) +
    `<path d="M${s} 0 L0 0 0 ${s}" ${strokeOnly(fg, sw)}/>` +
    "</svg>"
  );
};

const gridFine = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.5 + weight * 1.5);
  const h = r(s / 2);
  return (
    svgOpen("gridFine", s, s) +
    bgRect(s, s, bg) +
    `<path d="M${s} 0 L0 0 0 ${s} M${h} 0 L${h} ${s} M0 ${h} L${s} ${h}" ` +
    `${strokeOnly(fg, sw)}/>` +
    "</svg>"
  );
};

const linesHorizontal = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const band = r(s * (0.25 + weight * 0.25));
  return (
    svgOpen("linesHorizontal", s, s) +
    bgRect(s, s, bg) +
    `<rect width="${s}" height="${band}" fill="${fg}"/>` +
    "</svg>"
  );
};

const linesVertical = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const w = r(s * (0.25 + weight * 0.25));
  return (
    svgOpen("linesVertical", s, s) +
    bgRect(s, s, bg) +
    `<rect width="${w}" height="${s}" fill="${fg}"/>` +
    "</svg>"
  );
};

const diagonal = (p) => {
  // 45deg stripes; three offset bands so it tiles seamlessly.
  const { scale: s, fg, bg, weight } = normParams(p);
  const band = r(s * (0.2 + weight * 0.3));
  return (
    svgOpen("diagonal", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${s} L${s} 0 M${r(-s)} ${r(s / 2)} L${r(s / 2)} ${r(-s)} ` +
    `M${r(s / 2)} ${r(s + s / 2)} L${r(s + s / 2)} ${r(s / 2)}" ` +
    `stroke="${fg}" stroke-width="${band}"/>` +
    "</svg>"
  );
};

const diagonalReverse = (p) => {
  // 135deg stripes (mirror of `diagonal`).
  const { scale: s, fg, bg, weight } = normParams(p);
  const band = r(s * (0.2 + weight * 0.3));
  return (
    svgOpen("diagonalReverse", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 0 L${s} ${s} M${r(s / 2)} ${r(-s)} L${r(s + s / 2)} ${r(s / 2)} ` +
    `M${r(-s)} ${r(s / 2)} L${r(s / 2)} ${r(s + s / 2)}" ` +
    `stroke="${fg}" stroke-width="${band}"/>` +
    "</svg>"
  );
};

const crosshatch = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.5 + weight * 2);
  return (
    svgOpen("crosshatch", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 0 L${s} ${s} M0 ${s} L${s} 0" ${strokeOnly(fg, sw)}/>` +
    "</svg>"
  );
};

const chevronUp = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  const q = r(s / 4);
  return (
    svgOpen("chevronUp", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${q} L${h} ${r(-q)} L${s} ${q} L${s} ${h} L${h} ${q} L0 ${h} Z" fill="${fg}"/>` +
    `<path d="M0 ${r(s + q)} L${h} ${q} L${s} ${r(s + q)} L${s} ${s} L${h} ${r(s - q)} L0 ${s} Z" fill="${fg}"/>` +
    "</svg>"
  );
};

const chevronDown = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  const q = r(s / 4);
  return (
    svgOpen("chevronDown", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${h} L${h} ${r(s + q)} L${s} ${h} L${s} ${q} L${h} ${r(h + q)} L0 ${q} Z" fill="${fg}"/>` +
    `<path d="M0 ${r(-h)} L${h} ${q} L${s} ${r(-h)} L${s} ${r(-q)} L${h} ${r(q + h)} L0 ${r(-q)} Z" fill="${fg}"/>` +
    "</svg>"
  );
};

const zigzag = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 4);
  const h = r(s / 2);
  return (
    svgOpen("zigzag", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${h} L${h} 0 L${s} ${h}" ${strokeOnly(fg, sw)}/>` +
    `<path d="M0 ${s} L${h} ${h} L${s} ${s}" ${strokeOnly(fg, sw)}/>` +
    "</svg>"
  );
};

const wave = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 3);
  const h = r(s / 2);
  const q = r(s / 4);
  return (
    svgOpen("wave", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${h} Q${q} 0 ${h} ${h} T${s} ${h}" ${strokeOnly(fg, sw)}/>` +
    "</svg>"
  );
};

const waveTall = (p) => {
  // Larger-amplitude wave: crest/trough reach near the tile edges.
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 3);
  const h = r(s / 2);
  const q = r(s / 4);
  const top = r(s * 0.12);
  const bot = r(s * 0.88);
  return (
    svgOpen("waveTall", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${h} Q${q} ${top} ${h} ${h} T${s} ${h}" ${strokeOnly(fg, sw)}/>` +
    `<path d="M0 ${h} Q${q} ${bot} ${h} ${h} T${s} ${h}" ${strokeOnly(fg, sw)}/>` +
    "</svg>"
  );
};

const triangles = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  return (
    svgOpen("triangles", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${s} L${h} 0 L${s} ${s} Z" fill="${fg}"/>` +
    "</svg>"
  );
};

const trianglesAlt = (p) => {
  // Alternating up/down triangles across the tile.
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  return (
    svgOpen("trianglesAlt", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${s} L${h} 0 L${s} ${s} Z" fill="${fg}"/>` +
    `<path d="M${h} 0 L${s} ${s} L${s} 0 Z" fill="${fg}"/>` +
    `<path d="M0 0 L${h} 0 L0 ${s} Z" fill="${fg}"/>` +
    "</svg>"
  );
};

const diamonds = (p) => {
  // Outline diamond.
  const { scale: s, fg, bg, weight } = normParams(p);
  const h = s / 2;
  const sw = r(0.5 + weight * 2);
  return (
    svgOpen("diamonds", s, s) +
    bgRect(s, s, bg) +
    polyStroke(
      [
        [h, 0],
        [s, h],
        [h, s],
        [0, h],
      ],
      fg,
      sw
    ) +
    "</svg>"
  );
};

const diamondsFilled = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const h = s / 2;
  return (
    svgOpen("diamondsFilled", s, s) +
    bgRect(s, s, bg) +
    poly(
      [
        [h, 0],
        [s, h],
        [h, s],
        [0, h],
      ],
      fg
    ) +
    "</svg>"
  );
};

const hexagons = (p) => {
  // A single centred flat-top hexagon outline, tiled on the square grid. Reads
  // as a field of discrete hexagons (not an interlocking honeycomb — that would
  // need an offset/brick tile; kept simple + seamless by construction).
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const sw = r(0.5 + weight * 2);
  return (
    svgOpen("hexagons", s, s) +
    bgRect(s, s, bg) +
    polyStroke(regPts(c, c, s * 0.42, 6, 0), fg, sw) +
    "</svg>"
  );
};

const quatrefoil = (p) => {
  // Four overlapping rings forming a quatrefoil.
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const d = s * 0.17;
  const rad = s * 0.22;
  const sw = r(0.5 + weight * 1.8);
  return (
    svgOpen("quatrefoil", s, s) +
    bgRect(s, s, bg) +
    ringEl(c, c - d, rad, fg, sw) +
    ringEl(c + d, c, rad, fg, sw) +
    ringEl(c, c + d, rad, fg, sw) +
    ringEl(c - d, c, rad, fg, sw) +
    "</svg>"
  );
};

const scales = (p) => {
  // Fish scales: a downward scallop arc + two corner half-arcs for the offset row.
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const sw = r(0.8 + weight * 2);
  const rad = s / 2;
  return (
    svgOpen("scales", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 0 A ${r(rad)} ${r(rad)} 0 0 0 ${s} 0" ${strokeOnly(fg, sw)}/>` +
    `<path d="M${r(-c)} ${s} A ${r(rad)} ${r(rad)} 0 0 0 ${c} ${s}" ${strokeOnly(fg, sw)}/>` +
    `<path d="M${c} ${s} A ${r(rad)} ${r(rad)} 0 0 0 ${r(s + c)} ${s}" ${strokeOnly(fg, sw)}/>` +
    "</svg>"
  );
};

const seigaiha = (p) => {
  // Concentric wave fans (three arcs), centred bottom + offset corners.
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const sw = r(0.7 + weight * 1.6);
  const arc = (cx, cy, rad) =>
    `<path d="M${r(cx - rad)} ${r(cy)} A ${r(rad)} ${r(rad)} 0 0 1 ${r(cx + rad)} ${r(cy)}" ${strokeOnly(fg, sw)}/>`;
  const fan = (cx, cy) =>
    arc(cx, cy, s * 0.48) + arc(cx, cy, s * 0.33) + arc(cx, cy, s * 0.18);
  return (
    svgOpen("seigaiha", s, s) +
    bgRect(s, s, bg) +
    fan(c, s) +
    fan(0, c) +
    fan(s, c) +
    "</svg>"
  );
};

const arcs = (p) => {
  // Overlapping quarter arcs in opposite corners.
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.8 + weight * 2);
  const rad = s / 2;
  return (
    svgOpen("arcs", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${r(rad)} A ${r(rad)} ${r(rad)} 0 0 1 ${r(rad)} 0" ${strokeOnly(fg, sw)}/>` +
    `<path d="M${s} ${r(rad)} A ${r(rad)} ${r(rad)} 0 0 0 ${r(rad)} ${s}" ${strokeOnly(fg, sw)}/>` +
    "</svg>"
  );
};

const plus = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const arm = s * (0.18 + weight * 0.22);
  const t = s * (0.06 + weight * 0.1);
  return (
    svgOpen("plus", s, s) +
    bgRect(s, s, bg) +
    `<rect x="${r(c - t)}" y="${r(c - arm)}" width="${r(t * 2)}" height="${r(arm * 2)}" fill="${fg}"/>` +
    `<rect x="${r(c - arm)}" y="${r(c - t)}" width="${r(arm * 2)}" height="${r(t * 2)}" fill="${fg}"/>` +
    "</svg>"
  );
};

const crosses = (p) => {
  // Small ✕ marks, centre + tiling corners.
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const a = s * 0.16;
  const sw = r(1 + weight * 2);
  const x = (cx, cy) =>
    segEl(cx - a, cy - a, cx + a, cy + a, fg, sw) +
    segEl(cx - a, cy + a, cx + a, cy - a, fg, sw);
  return (
    svgOpen("crosses", s, s) +
    bgRect(s, s, bg) +
    x(c, c) +
    x(0, 0) +
    x(s, 0) +
    x(0, s) +
    x(s, s) +
    "</svg>"
  );
};

const squares = (p) => {
  // Filled square, centred (tiles into a checker-of-squares field via the gap).
  const { scale: s, fg, bg, weight } = normParams(p);
  const side = s * (0.34 + weight * 0.2);
  const o = (s - side) / 2;
  return (
    svgOpen("squares", s, s) +
    bgRect(s, s, bg) +
    `<rect x="${r(o)}" y="${r(o)}" width="${r(side)}" height="${r(side)}" fill="${fg}"/>` +
    "</svg>"
  );
};

const nestedSquares = (p) => {
  // Concentric square outlines.
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.5 + weight * 1.6);
  const box = (frac) => {
    const side = s * frac;
    const o = (s - side) / 2;
    return `<rect x="${r(o)}" y="${r(o)}" width="${r(side)}" height="${r(side)}" ${strokeOnly(fg, sw)}/>`;
  };
  return (
    svgOpen("nestedSquares", s, s) +
    bgRect(s, s, bg) +
    box(0.78) +
    box(0.46) +
    "</svg>"
  );
};

const rings = (p) => {
  // Concentric circle outlines.
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const sw = r(0.5 + weight * 1.6);
  return (
    svgOpen("rings", s, s) +
    bgRect(s, s, bg) +
    ringEl(c, c, s * 0.4, fg, sw) +
    ringEl(c, c, s * 0.22, fg, sw) +
    "</svg>"
  );
};

const stars = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const c = s / 2;
  return (
    svgOpen("stars", s, s) +
    bgRect(s, s, bg) +
    starPath(c, c, s * 0.42, s * 0.17, fg) +
    "</svg>"
  );
};

const hearts = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const u = unitBox(s / 2, s / 2, s * 0.82);
  const d =
    `M${u(0.5, 0.85)} C${u(0.05, 0.5)} ${u(0.1, 0.16)} ${u(0.32, 0.16)} ` +
    `C${u(0.45, 0.16)} ${u(0.5, 0.3)} ${u(0.5, 0.38)} ` +
    `C${u(0.5, 0.3)} ${u(0.55, 0.16)} ${u(0.68, 0.16)} ` +
    `C${u(0.9, 0.16)} ${u(0.95, 0.5)} ${u(0.5, 0.85)} Z`;
  return (
    svgOpen("hearts", s, s) +
    bgRect(s, s, bg) +
    `<path d="${d}" fill="${fg}"/>` +
    "</svg>"
  );
};

const confetti = (p) => {
  // Scattered small angled bars.
  const { scale: s, fg, bg } = normParams(p);
  const w = s * 0.16;
  const bar = (cx, cy, rot) =>
    `<rect x="${r(cx - w / 2)}" y="${r(cy - w / 6)}" width="${r(w)}" height="${r(w / 3)}" ` +
    `rx="${r(w / 6)}" fill="${fg}" transform="rotate(${rot} ${r(cx)} ${r(cy)})"/>`;
  return (
    svgOpen("confetti", s, s) +
    bgRect(s, s, bg) +
    bar(s * 0.25, s * 0.28, 25) +
    bar(s * 0.7, s * 0.35, -40) +
    bar(s * 0.4, s * 0.66, -15) +
    bar(s * 0.78, s * 0.74, 55) +
    bar(s * 0.15, s * 0.8, 70) +
    "</svg>"
  );
};

const sprinkles = (p) => {
  // Short diagonal dashes scattered.
  const { scale: s, fg, bg, weight } = normParams(p);
  const a = s * 0.1;
  const sw = r(1.2 + weight * 1.8);
  const d = (cx, cy) => segEl(cx - a, cy + a, cx + a, cy - a, fg, sw);
  return (
    svgOpen("sprinkles", s, s) +
    bgRect(s, s, bg) +
    d(s * 0.22, s * 0.25) +
    d(s * 0.68, s * 0.2) +
    d(s * 0.45, s * 0.52) +
    d(s * 0.82, s * 0.62) +
    d(s * 0.2, s * 0.78) +
    d(s * 0.6, s * 0.82) +
    "</svg>"
  );
};

const terrazzo = (p) => {
  // Scattered chips (small polygons + a dot).
  const { scale: s, fg, bg } = normParams(p);
  return (
    svgOpen("terrazzo", s, s) +
    bgRect(s, s, bg) +
    poly(regPts(s * 0.28, s * 0.3, s * 0.11, 5, 0.4), fg) +
    poly(regPts(s * 0.72, s * 0.28, s * 0.1, 3, 0.2), fg) +
    poly(regPts(s * 0.66, s * 0.72, s * 0.12, 6, 0.1), fg) +
    dot(s * 0.28, s * 0.74, s * 0.08, fg) +
    "</svg>"
  );
};

const blobs = (p) => {
  // Organic blob, centred.
  const { scale: s, fg, bg } = normParams(p);
  const u = unitBox(s / 2, s / 2, s * 0.86);
  const d =
    `M${u(0.5, 0.08)} C${u(0.76, 0.08)} ${u(0.93, 0.28)} ${u(0.88, 0.52)} ` +
    `C${u(0.84, 0.76)} ${u(0.7, 0.93)} ${u(0.47, 0.9)} ` +
    `C${u(0.24, 0.87)} ${u(0.08, 0.71)} ${u(0.12, 0.46)} ` +
    `C${u(0.16, 0.22)} ${u(0.28, 0.08)} ${u(0.5, 0.08)} Z`;
  return (
    svgOpen("blobs", s, s) +
    bgRect(s, s, bg) +
    `<path d="${d}" fill="${fg}"/>` +
    "</svg>"
  );
};

const leaves = (p) => {
  // Leaf (two arcs) + midrib (cut out in bg so the vein reads on the tint).
  const { scale: s, fg, bg, weight } = normParams(p);
  const u = unitBox(s / 2, s / 2, s * 0.78);
  const sw = r(0.6 + weight * 1.4);
  const leaf =
    `M${u(0.5, 0.06)} C${u(0.86, 0.28)} ${u(0.86, 0.72)} ${u(0.5, 0.94)} ` +
    `C${u(0.14, 0.72)} ${u(0.14, 0.28)} ${u(0.5, 0.06)} Z`;
  return (
    svgOpen("leaves", s, s) +
    bgRect(s, s, bg) +
    `<path d="${leaf}" fill="${fg}"/>` +
    `<path d="M${u(0.5, 0.12)} L${u(0.5, 0.88)}" fill="none" stroke="${bg}" stroke-width="${sw}"/>` +
    "</svg>"
  );
};

const herringbone = (p) => {
  // Herringbone: interlocking ±45° bars forming an offset diagonal weave.
  // SEAMLESS BY CONSTRUCTION: every edge is crossed at its two quarter points
  // {q, t3}, and the opposite edge is crossed at the SAME two points, so strokes
  // continue across every seam with no break. (The old 4-segment V-tile crossed
  // the left edge at {0, s} but the right edge only at {s/2} — a visible
  // horizontal seam; this replaces it.) Verified by the edge-crossing test in
  // patternGenerators.test.js.
  const { scale: s, fg, bg, weight } = normParams(p);
  const q = s / 4;
  const t3 = (3 * s) / 4;
  const sw = r(1.5 + weight * 3);
  return (
    svgOpen("herringbone", s, s) +
    bgRect(s, s, bg) +
    // two "\" bars (slope +1): left@q->bottom@t3, top@q->right@t3
    segEl(0, q, t3, s, fg, sw) +
    segEl(q, 0, s, t3, fg, sw) +
    // two "/" bars (slope -1): left@t3->top@t3, bottom@q->right@q
    segEl(0, t3, t3, 0, fg, sw) +
    segEl(q, s, s, q, fg, sw) +
    "</svg>"
  );
};

const basketweave = (p) => {
  // Interwoven bars (checker of horizontal/vertical fills).
  const { scale: s, fg, bg } = normParams(p);
  const h = s / 2;
  const g = s * 0.08;
  return (
    svgOpen("basketweave", s, s) +
    bgRect(s, s, bg) +
    `<rect x="${r(g)}" y="${r(g)}" width="${r(h - 2 * g)}" height="${r(h - 2 * g)}" fill="${fg}"/>` +
    `<rect x="${r(h + g)}" y="${r(h + g)}" width="${r(h - 2 * g)}" height="${r(h - 2 * g)}" fill="${fg}"/>` +
    "</svg>"
  );
};

const brick = (p) => {
  // Running-bond brick outline (offset rows).
  const { scale: s, fg, bg, weight } = normParams(p);
  const h = s / 2;
  const sw = r(0.6 + weight * 1.6);
  return (
    svgOpen("brick", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 0 L${s} 0 M0 ${h} L${s} ${h}" ${strokeOnly(fg, sw)}/>` +
    segEl(h, 0, h, h, fg, sw) +
    segEl(0, h, 0, s, fg, sw) +
    segEl(s, h, s, s, fg, sw) +
    "</svg>"
  );
};

const trellis = (p) => {
  // Diamond lattice (crossing diagonals) — an outline diamond grid.
  const { scale: s, fg, bg, weight } = normParams(p);
  const h = s / 2;
  const sw = r(0.6 + weight * 1.6);
  return (
    svgOpen("trellis", s, s) +
    bgRect(s, s, bg) +
    `<path d="M${h} 0 L${s} ${h} L${h} ${s} L0 ${h} Z" ${strokeOnly(fg, sw)}/>` +
    `<path d="M0 0 L${h} 0 L0 ${h} Z M${s} 0 L${h} 0 L${s} ${h} Z ` +
    `M0 ${s} L${h} ${s} L0 ${h} Z M${s} ${s} L${h} ${s} L${s} ${h} Z" ${strokeOnly(fg, sw)}/>` +
    "</svg>"
  );
};

const weave = (p) => {
  // Plain weave: alternating over/under bars.
  const { scale: s, fg, bg } = normParams(p);
  const h = s / 2;
  const t = s * 0.34;
  const o = (h - t) / 2;
  return (
    svgOpen("weave", s, s) +
    bgRect(s, s, bg) +
    `<rect x="${r(o)}" y="0" width="${r(t)}" height="${r(h)}" fill="${fg}"/>` +
    `<rect x="${r(h + o)}" y="${r(h)}" width="${r(t)}" height="${r(h)}" fill="${fg}"/>` +
    `<rect x="0" y="${r(h + o)}" width="${r(h)}" height="${r(t)}" fill="${fg}"/>` +
    `<rect x="${r(h)}" y="${r(o)}" width="${r(h)}" height="${r(t)}" fill="${fg}"/>` +
    "</svg>"
  );
};

const dotsGrid = (p) => {
  // Grid lines with a dot at each cell centre.
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const sw = r(0.5 + weight * 1.5);
  const rad = s * (0.08 + weight * 0.08);
  return (
    svgOpen("dotsGrid", s, s) +
    bgRect(s, s, bg) +
    `<path d="M${s} 0 L0 0 0 ${s}" ${strokeOnly(fg, sw)}/>` +
    dot(c, c, rad, fg) +
    "</svg>"
  );
};

const ticks = (p) => {
  // Short vertical tick dashes on a lattice.
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = s / 2;
  const len = s * 0.28;
  const sw = r(1 + weight * 2);
  const tick = (cx, cy) => segEl(cx, cy - len / 2, cx, cy + len / 2, fg, sw);
  return (
    svgOpen("ticks", s, s) +
    bgRect(s, s, bg) +
    tick(c, c) +
    tick(0, 0) +
    tick(s, 0) +
    tick(0, s) +
    tick(s, s) +
    "</svg>"
  );
};

const waveDots = (p) => {
  // Wave line with dots in the troughs.
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 2.5);
  const h = r(s / 2);
  const q = r(s / 4);
  const rad = s * 0.08;
  return (
    svgOpen("waveDots", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${h} Q${q} 0 ${h} ${h} T${s} ${h}" ${strokeOnly(fg, sw)}/>` +
    dot(q, s * 0.82, rad, fg) +
    dot(s * 0.75, s * 0.82, rad, fg) +
    "</svg>"
  );
};

const chevronBold = (p) => {
  // Heavier chevron bands.
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  const t = r(s / 3);
  return (
    svgOpen("chevronBold", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${t} L${h} ${r(-t)} L${s} ${t} L${s} ${r(t + t)} L${h} ${r(t)} L0 ${r(t + t)} Z" fill="${fg}"/>` +
    `<path d="M0 ${r(s + t)} L${h} ${r(s - t)} L${s} ${r(s + t)} L${s} ${r(s + t + t)} L${h} ${r(s + t)} L0 ${r(s + t + t)} Z" fill="${fg}"/>` +
    "</svg>"
  );
};

// ---- registry --------------------------------------------------------------

// Ordered list the UI renders — matches the design deck's Pattern Library order
// (03-pattern-library). id is stable (stored in the data-URI marker) and is
// letters-only so parsePatternCss's marker regex round-trips it.
export const PATTERNS = [
  { id: "dots", label: "Dots", generator: dots },
  { id: "dotsOffset", label: "Dots offset", generator: dotsOffset },
  { id: "dotsHollow", label: "Dots hollow", generator: dotsHollow },
  { id: "grid", label: "Grid", generator: grid },
  { id: "gridFine", label: "Grid fine", generator: gridFine },
  { id: "linesHorizontal", label: "Lines horizontal", generator: linesHorizontal },
  { id: "linesVertical", label: "Lines vertical", generator: linesVertical },
  { id: "diagonal", label: "Diagonal", generator: diagonal },
  { id: "diagonalReverse", label: "Diagonal reverse", generator: diagonalReverse },
  { id: "crosshatch", label: "Cross-hatch", generator: crosshatch },
  { id: "chevronUp", label: "Chevron up", generator: chevronUp },
  { id: "chevronDown", label: "Chevron down", generator: chevronDown },
  { id: "zigzag", label: "Zigzag", generator: zigzag },
  { id: "wave", label: "Wave", generator: wave },
  { id: "waveTall", label: "Wave tall", generator: waveTall },
  { id: "triangles", label: "Triangles", generator: triangles },
  { id: "trianglesAlt", label: "Triangles alt", generator: trianglesAlt },
  { id: "diamonds", label: "Diamonds", generator: diamonds },
  { id: "diamondsFilled", label: "Diamonds filled", generator: diamondsFilled },
  { id: "hexagons", label: "Hexagons", generator: hexagons },
  { id: "quatrefoil", label: "Quatrefoil", generator: quatrefoil },
  { id: "scales", label: "Scales", generator: scales },
  { id: "seigaiha", label: "Seigaiha", generator: seigaiha },
  { id: "arcs", label: "Arcs", generator: arcs },
  { id: "plus", label: "Plus", generator: plus },
  { id: "crosses", label: "Crosses", generator: crosses },
  { id: "squares", label: "Squares", generator: squares },
  { id: "nestedSquares", label: "Nested squares", generator: nestedSquares },
  { id: "rings", label: "Rings", generator: rings },
  { id: "stars", label: "Stars", generator: stars },
  { id: "hearts", label: "Hearts", generator: hearts },
  { id: "confetti", label: "Confetti", generator: confetti },
  { id: "sprinkles", label: "Sprinkles", generator: sprinkles },
  { id: "terrazzo", label: "Terrazzo", generator: terrazzo },
  { id: "blobs", label: "Blobs", generator: blobs },
  { id: "leaves", label: "Leaves", generator: leaves },
  { id: "herringbone", label: "Herringbone", generator: herringbone },
  { id: "basketweave", label: "Basketweave", generator: basketweave },
  { id: "brick", label: "Brick", generator: brick },
  { id: "trellis", label: "Trellis", generator: trellis },
  { id: "weave", label: "Weave", generator: weave },
  { id: "dotsGrid", label: "Dots + grid", generator: dotsGrid },
  { id: "ticks", label: "Ticks", generator: ticks },
  { id: "waveDots", label: "Wave + dots", generator: waveDots },
  { id: "chevronBold", label: "Chevron bold", generator: chevronBold },
];

export const PATTERN_BY_ID = PATTERNS.reduce((acc, p) => {
  acc[p.id] = p;
  return acc;
}, {});

// Generate the SVG markup for a pattern id + params. Returns "" for unknown ids
// (defensive: never throw into a render path).
export const generatePatternSvg = (id, params) => {
  const entry = PATTERN_BY_ID[id];
  if (!entry) return "";
  return entry.generator(params || {});
};

export { normParams };
